import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Building2, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Home,
  Building,
  Factory,
  Hammer,
  Layers,
  Settings,
  Star,
  Users,
  Mail,
  Mountain
} from "lucide-react";

// Import masonry images
import masonBrickwork from "@/assets/mason-brickwork.jpg";
import masonBrickworkConstruction from "@/assets/mason-brickwork-construction.jpg";
import stoneMasonryWork from "@/assets/stone-masonry-work.jpg";
import concreteBlockMasonry from "@/assets/concrete-block-masonry.jpg";
import masonryRepairRestoration from "@/assets/masonry-repair-restoration.jpg";
import stuccoApplication from "@/assets/stucco-application-ontario.jpg";
import sidingInstallation from "@/assets/siding-installation-ontario.jpg";
import stoneVeneerInstallation from "@/assets/stone-veneer-installation.jpg";

const MasonryServices = () => {
  const faqData = [
    {
      question: "Do you provide masonry services in Niagara Region, Hamilton, and Toronto GTA?",
      answer: "Yes! Blue Knight Contracting provides professional masonry services throughout the Niagara Region (including Niagara Falls, St. Catharines, Welland), Hamilton, Burlington, and the entire Toronto GTA. We serve both residential and commercial clients with expert brick, stone, and concrete block work."
    },
    {
      question: "What masonry services do you offer in Hamilton and Niagara?",
      answer: "We offer complete masonry services including brick work, stone masonry, concrete block construction, chimney repair and construction, retaining walls, masonry restoration, stucco application, and heritage building restoration throughout Hamilton, Niagara Region, and Toronto GTA."
    },
    {
      question: "Do you handle commercial masonry projects in the GTA?",
      answer: "Absolutely. Our skilled masons handle commercial projects throughout Toronto GTA, Hamilton, and Niagara Region including office buildings, retail spaces, industrial facilities, and large-scale masonry construction for businesses across Ontario."
    },
    {
      question: "Can you repair old brick and stone work in Niagara heritage homes?",
      answer: "Yes. We specialize in heritage restoration and masonry repair throughout Niagara-on-the-Lake, St. Catharines, Hamilton, and historic neighborhoods across Ontario. Our masons use traditional techniques and period-accurate materials for authentic restoration."
    },
    {
      question: "How much do masonry services cost in Hamilton and Niagara Region?",
      answer: "Masonry costs vary based on project scope, materials, and complexity. We provide free, detailed quotes for all masonry work in Hamilton, Niagara Region, and Toronto GTA. Contact us for an accurate estimate for your brick, stone, or concrete block project."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Masonry Services - Niagara Region, Hamilton, Toronto GTA",
    description: "Professional masonry contractor serving Niagara Region, Hamilton, and Toronto GTA with expert brick work, stone masonry, concrete block construction, and masonry repair for residential and commercial properties.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction",
      telephone: "(416) 555-BLUE",
      email: "info@blueknightcontracting.ca"
    },
    serviceType: "Masonry Services",
    areaServed: [
      {
        "@type": "City",
        "name": "Niagara Falls, Ontario"
      },
      {
        "@type": "City",
        "name": "St. Catharines, Ontario"
      },
      {
        "@type": "City",
        "name": "Hamilton, Ontario"
      },
      {
        "@type": "City",
        "name": "Burlington, Ontario"
      },
      {
        "@type": "City",
        "name": "Toronto, Ontario"
      },
      {
        "@type": "City",
        "name": "Mississauga, Ontario"
      }
    ],
    offers: {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "CAD"
    }
  };

  const masonryServices = [
    {
      title: "Brick Masonry",
      description: "Professional brick work for new construction, repairs, and restoration projects.",
      icon: Building2,
      services: [
        "New brick construction",
        "Brick repair and replacement",
        "Mortar joint repointing",
        "Brick cleaning and restoration",
        "Structural brick work",
        "Decorative brick features"
      ]
    },
    {
      title: "Stone Masonry",
      description: "Natural stone installation, repair, and custom stonework for residential and commercial projects.",
      icon: Mountain,
      services: [
        "Natural stone veneer",
        "Stone wall construction",
        "Fieldstone masonry",
        "Limestone installation",
        "Granite and marble work",
        "Custom stone features"
      ]
    },
    {
      title: "Concrete Block",
      description: "Concrete masonry unit (CMU) construction for foundations, walls, and structural applications.",
      icon: Layers,
      services: [
        "Foundation block work",
        "Structural CMU walls",
        "Decorative block installation",
        "Retaining wall construction",
        "Commercial block buildings",
        "Reinforced masonry systems"
      ]
    },
    {
      title: "Chimney Services",
      description: "Complete chimney construction, repair, and maintenance for safe, functional fireplaces.",
      icon: Home,
      services: [
        "New chimney construction",
        "Chimney repair and restoration",
        "Crown and cap installation",
        "Flue liner installation",
        "Chimney cleaning and inspection",
        "Structural chimney repairs"
      ]
    },
    {
      title: "Commercial Masonry",
      description: "Large-scale masonry projects for commercial, industrial, and institutional buildings.",
      icon: Building,
      services: [
        "Commercial building facades",
        "Industrial masonry construction",
        "Institutional projects",
        "High-rise masonry work",
        "Precast concrete installation",
        "Architectural masonry features"
      ]
    },
    {
      title: "Masonry Repair",
      description: "Comprehensive repair and restoration services for damaged or deteriorating masonry.",
      icon: Settings,
      services: [
        "Structural masonry repairs",
        "Heritage building restoration",
        "Water damage repair",
        "Crack repair and stabilization",
        "Masonry cleaning services",
        "Protective coatings application"
      ]
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region", 
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Cornwall", "Belleville", "Pembroke", "Petawawa",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph", "Sarnia", "Chatham",
    "Northern Ontario",
    "Sudbury", "Thunder Bay", "North Bay", "Sault Ste. Marie", "Timmins", "Kenora"
  ];

  return (
    <>
      <SEOMeta 
        title="Masonry Contractor — Stone & Brick Work | Niagara • Hamilton • GTA"
        description="Skilled masonry for brick repair, stonework and foundations across Niagara, Hamilton & the GTA. Durable craftsmanship for homes and businesses."
        keywords="masonry contractor Niagara, Hamilton brick repair, GTA stonework, retaining walls, block work, chimney repair, stone veneer"
        canonicalUrl="https://blueknightcontracting.ca/services/masonry"
      />
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Professional Masonry Services in Niagara Region, Hamilton & Toronto GTA
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting is your trusted masonry contractor serving <strong>Niagara Region</strong>, <strong>Hamilton</strong>, and the <strong>Toronto GTA</strong>. From brick and stone work to concrete block construction and heritage restoration, our licensed masons deliver quality craftsmanship with over 20 years of experience across Ontario.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Link to="/services">View All Services</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={masonBrickworkConstruction} 
                  alt="Professional mason building brick wall in Ontario showing expert craftsmanship"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Comprehensive Masonry Solutions for Niagara, Hamilton & GTA
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our licensed masons serve <Link to="/locations/niagara" className="text-primary hover:underline">Niagara Region</Link>, <Link to="/hamilton" className="text-primary hover:underline">Hamilton</Link>, and <Link to="/toronto" className="text-primary hover:underline">Toronto GTA</Link> with complete masonry services using traditional techniques and modern materials for lasting, beautiful results.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {masonryServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Local Masonry Expertise Section */}
        <section className="py-20 bg-gradient-to-br from-muted/30 to-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Local Masonry Expertise in Your Community
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Serving homeowners and businesses with quality masonry work tailored to the unique needs of each region
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4 text-primary">Niagara Region Masonry Projects</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Heritage Restoration:</strong> Specializing in Niagara-on-the-Lake historic brick restoration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Winery Projects:</strong> Stone masonry for Niagara wine country estates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Residential Brick:</strong> New home construction and additions across Niagara Falls and St. Catharines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Retaining Walls:</strong> Landscape masonry for Grimsby and Lincoln properties</span>
                  </li>
                </ul>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4 text-primary">Hamilton Masonry Specialists</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Industrial Masonry:</strong> Commercial block work for Hamilton's industrial sector</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Residential Services:</strong> Brick repair and stone veneer in Ancaster and Dundas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Heritage Work:</strong> Historic brick restoration in downtown Hamilton</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Burlington Projects:</strong> Upscale stone masonry and chimney services</span>
                  </li>
                </ul>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4 text-primary">Toronto GTA Masonry Work</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>High-Rise Projects:</strong> Commercial masonry for Toronto condo and office buildings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Luxury Homes:</strong> Custom stone and brick work in Oakville and Mississauga</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Urban Restoration:</strong> Historic masonry repair in Toronto neighborhoods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Suburban Work:</strong> Residential brick and stone throughout Brampton, Vaughan, Markham</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Masonry Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of masonry work across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={masonBrickwork} 
                    alt="Ontario mason constructing brick wall for commercial property"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Brick Construction</h3>
                  <p className="text-muted-foreground">
                    Precision brick work for residential and commercial projects
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={stoneMasonryWork} 
                    alt="Ontario mason installing natural stone veneer on residential home"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Stone Masonry</h3>
                  <p className="text-muted-foreground">
                    Natural stone installation and custom stonework
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={concreteBlockMasonry} 
                    alt="Concrete block masonry construction for commercial building in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Concrete Block</h3>
                  <p className="text-muted-foreground">
                    Structural and decorative concrete masonry units
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={masonryRepairRestoration} 
                    alt="Masonry repair and restoration work on heritage building in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Restoration Work</h3>
                  <p className="text-muted-foreground">
                    Heritage restoration and masonry repair services
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Specialty Exterior Services */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Specialty Exterior Finishes
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Expert installation and repair of stucco, siding, masonry, and stone veneer for beautiful, durable exteriors
              </p>
            </div>

            <div className="space-y-12">
              {/* Stucco Services */}
              <Card className="overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative h-[400px]">
                    <img 
                      src={stuccoApplication}
                      alt="Professional stucco application on Ontario home exterior"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <h3 className="text-3xl font-bold mb-4">Stucco Services</h3>
                    <p className="text-lg text-muted-foreground mb-6">
                      Professional stucco installation, repair, and restoration for residential and commercial properties. Our experienced team delivers beautiful, weather-resistant finishes that enhance curb appeal and protect your building.
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>New stucco installation - traditional and EIFS systems</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Stucco repair and patching for cracks and damage</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Texture matching and custom finishes</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Waterproofing and moisture remediation</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Color matching and painting services</span>
                      </li>
                    </ul>
                    <Button asChild variant="default">
                      <Link to="/contact">Get Stucco Quote</Link>
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Siding Services */}
              <Card className="overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="p-8 flex flex-col justify-center order-2 md:order-1">
                    <h3 className="text-3xl font-bold mb-4">Siding Installation & Repair</h3>
                    <p className="text-lg text-muted-foreground mb-6">
                      Expert siding installation and repair services for all siding types. We install vinyl, fiber cement, wood, and composite siding that protects your home while enhancing its appearance and value.
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Vinyl siding installation - low maintenance and durable</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Fiber cement siding - James Hardie certified installers</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Wood and composite siding options</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Siding repair and replacement</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Complete siding removal and re-siding projects</span>
                      </li>
                    </ul>
                    <Button asChild variant="default">
                      <Link to="/contact">Get Siding Quote</Link>
                    </Button>
                  </div>
                  <div className="relative h-[400px] order-1 md:order-2">
                    <img 
                      src={sidingInstallation}
                      alt="Professional siding installation on Ontario residential home"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>
              </Card>

              {/* Traditional Masonry */}
              <Card className="overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative h-[400px]">
                    <img 
                      src={masonBrickwork}
                      alt="Expert masonry work - brick and stone construction in Ontario"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <h3 className="text-3xl font-bold mb-4">Traditional Masonry</h3>
                    <p className="text-lg text-muted-foreground mb-6">
                      Time-tested masonry construction using brick, stone, and concrete block. Our skilled masons combine traditional craftsmanship with modern techniques for structures that last generations.
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Brick and stone wall construction</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Concrete block foundations and walls</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Retaining walls and landscape masonry</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Chimneys and fireplaces</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Heritage restoration and period-accurate repairs</span>
                      </li>
                    </ul>
                    <Button asChild variant="default">
                      <Link to="/contact">Get Masonry Quote</Link>
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Stone Veneer */}
              <Card className="overflow-hidden">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="p-8 flex flex-col justify-center order-2 md:order-1">
                    <h3 className="text-3xl font-bold mb-4">Stone Veneer Installation</h3>
                    <p className="text-lg text-muted-foreground mb-6">
                      Transform your property with stunning natural or manufactured stone veneer. Add elegance, texture, and value with expertly installed stone finishes for interior and exterior applications.
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Natural stone veneer - limestone, granite, slate</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Manufactured stone veneer - realistic, lightweight options</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Accent walls and feature installations</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Exterior facades and entryways</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>Fireplace surrounds and interior features</span>
                      </li>
                    </ul>
                    <Button asChild variant="default">
                      <Link to="/contact">Get Stone Veneer Quote</Link>
                    </Button>
                  </div>
                  <div className="relative h-[400px] order-1 md:order-2">
                    <img 
                      src={stoneVeneerInstallation}
                      alt="Beautiful stone veneer installation on luxury Ontario home"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Our Masonry Process
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional masonry construction following time-tested techniques and modern standards
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-2">Design & Planning</h3>
                <p className="text-muted-foreground">
                  Site assessment, material selection, and detailed project planning
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-2">Foundation Prep</h3>
                <p className="text-muted-foreground">
                  Proper foundation preparation and layout for structural integrity
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-2">Skilled Construction</h3>
                <p className="text-muted-foreground">
                  Expert masonry construction using quality materials and techniques
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="text-xl font-semibold mb-2">Quality Finish</h3>
                <p className="text-muted-foreground">
                  Final inspection, cleanup, and protective treatments as needed
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Masonry?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Skilled Craftsmen</h3>
                <p className="text-muted-foreground">
                  Experienced masons with traditional and modern techniques
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-muted-foreground">
                  Premium masonry materials from trusted suppliers
                </p>
              </Card>

              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Structural Integrity</h3>
                <p className="text-muted-foreground">
                  Built to last with proper engineering and construction
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Serving communities across Ontario with reliable service
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Masonry Contractor Serving Niagara Region, Hamilton & Toronto GTA
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting is your local masonry expert proudly serving the <strong>Niagara Region</strong>, <strong>Hamilton</strong>, <strong>Burlington</strong>, and the entire <strong>Toronto GTA</strong> with professional brick, stone, and concrete block services
              </p>
            </div>

            {/* Key Service Areas Highlight */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="p-8 text-center border-2 border-primary/20 hover:border-primary/40 transition-colors">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Niagara Region Masonry</h3>
                <p className="text-muted-foreground mb-4">
                  Expert brick, stone & masonry services throughout Niagara Falls, St. Catharines, Welland, Niagara-on-the-Lake, Fort Erie, Grimsby, and all Niagara communities
                </p>
                <ul className="text-left space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Niagara Falls • St. Catharines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Welland • Niagara-on-the-Lake</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Fort Erie • Grimsby • Port Colborne</span>
                  </li>
                </ul>
              </Card>

              <Card className="p-8 text-center border-2 border-primary/20 hover:border-primary/40 transition-colors">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Hamilton Masonry Services</h3>
                <p className="text-muted-foreground mb-4">
                  Professional masonry contractors in Hamilton, Burlington, Stoney Creek, Ancaster, Dundas, and surrounding Hamilton areas
                </p>
                <ul className="text-left space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Hamilton • Burlington</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Stoney Creek • Ancaster</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Dundas • Waterdown • Flamborough</span>
                  </li>
                </ul>
              </Card>

              <Card className="p-8 text-center border-2 border-primary/20 hover:border-primary/40 transition-colors">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Toronto GTA Masonry</h3>
                <p className="text-muted-foreground mb-4">
                  Complete masonry services across Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Oakville, and the entire Greater Toronto Area
                </p>
                <ul className="text-left space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Toronto • Mississauga • Brampton</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Vaughan • Markham • Richmond Hill</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Oakville • Milton • Scarborough</span>
                  </li>
                </ul>
              </Card>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area");
                
                return (
                  <Card key={index} className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}>
                    <MapPin className={`h-6 w-6 mx-auto mb-2 ${isRegionHeader ? 'text-white' : 'text-primary'}`} />
                    <p className={`font-medium ${isRegionHeader ? 'text-white text-lg' : ''}`}>{area}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our masonry services in Ontario
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready for Expert Masonry Work in Niagara, Hamilton or Toronto GTA?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for professional brick, stone, and concrete block masonry services in Niagara Region, Hamilton, Burlington, and Toronto GTA. Get your free consultation and masonry quote.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Get Free Masonry Quote</Link>
            </Button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default MasonryServices;